import { find, map, sum, sumBy } from 'lodash-es';
import { formatNumber } from '~/components/NumberText';
import { formatDate } from '~/lib/date';
import {
  CalculatedProjectOfferPeriodsByName,
  EAReportGroupedMeter
} from '~/pages/dashboard/project/EAReportNew/reportTypes';

export const getEATemplate = (data?: {
  contactName?: string;
  companyName?: string;
  groupedMeters: EAReportGroupedMeter[];
}) => {
  if (!data) {
    return '';
  }

  const groupedMetersWithBestOffer = map(data.groupedMeters, (groupedMeter) => {
    const bestOffer = find(groupedMeter.offers, (offer) => offer.isBest);
    const bestOfferPeriod =
      find(bestOffer?.periods, (period) => period.isBest) || ({} as CalculatedProjectOfferPeriodsByName);
    return {
      ...groupedMeter,
      bestOffer,
      bestOfferPeriod
    };
  });

  return `<p>Dear ${data.contactName},</p>
<p>Please find the attached Evaluation & Analysis Report for the electricity tender conducted by Trans Tasman Energy Group (TTEG) on behalf of ${
    data.companyName
  } for the following site/s:</p>

${groupedMetersWithBestOffer
  .map(({ meters, name, bestOffer, bestOfferPeriod }) => {
    const contestableCostDiff =
      sumBy(bestOfferPeriod.environmentalRatePeriods, (item) => item.contestableCost) *
        (12 / Number(bestOfferPeriod?.periodName)) -
      sumBy(meters, (meter) => sum([meter.annualData.contestableCost]));
    return `
<div>
<p><strong style="border-bottom: 1px solid #000">${groupedMetersWithBestOffer.length > 1 ? name : ''}</strong></p>
  <table style="border-collapse: collapse; width: 100%;" border="1">
<tbody>
<tr>
<td><strong>NMI</strong></td>
<td><strong>Site Address</strong></td>
</tr>
${meters
  .map(
    (meter) => `<tr>
<td>${meter.meterNumber}</td>
<td>${[meter.streetAddress, meter.suburb, meter.state, meter.postCode].filter(Boolean).join(', ')}</td>
</tr>`
  )
  .join('\n')}
</tbody>
</table>
<p>
<strong>Evaluation and Analysis:</strong>
<br>
Based on our analysis, ${bestOffer?.retailer.name}'s ${
      bestOfferPeriod?.periodName
    } months offer was the most price competitive among all offers and is valid until ${formatDate(
      bestOffer?.validityEndDateTime,
      'hA dddd, D MMMM YYYY'
    )}
</p>
<ul>
<li>Compared to your current contract, ${bestOffer?.retailer.name}'s offer represents a contestable cost ${
      contestableCostDiff > 0 ? `increase` : `saving`
    } of approximately ${formatNumber(
      (Math.abs(contestableCostDiff) / sumBy(meters, (meter) => sum([meter.annualData.contestableCost]))) * 100,
      1
    )}% or $${formatNumber(Math.abs(contestableCostDiff), 0)} to your existing rates over ${
      bestOfferPeriod?.periodName
    } months.</li>
<li>Contestable costs included in the analysis are Energy, Renewable, Metering and Service fees, please note that ~${formatNumber(
      (sumBy(bestOfferPeriod.environmentalRatePeriods, (item) => item.totalCosts - item.contestableCost) /
        sumBy(bestOfferPeriod.environmentalRatePeriods, (item) => item.totalCosts)) *
        100,
      1
    )}% of your electricity bill is made up of non-contestable network and market charges that are adjusted annually by your distributor and AEMO respectively.</li>
<li>To avoid any further rate increases, we believe the most risk adverse approach is to accept ${
      bestOffer?.retailer.name
    }'s offer now.</li>
</ul>
</div>
`;
  })
  .join('<br/>')}

<br/>
<p>
<strong>Retailer Participation:</strong>
<br>
<em>TTEG issued a Request for Pricing (RFP) to all retailers that are licensed to provide large contract offers and we have included all the offers submitted.</em>
</p>

<p><strong>Next Steps:</strong><br>I will be in contact with you shortly to discuss the tender results.</p>`;
};
